import { localDayOf } from "../../shared/format/local-date.js";
import { router } from "../router/router.js";
import { documentStatusLabel, translateStatus, INTEGRITY_REPORT_STATUS_LABELS } from "../../domain/shared/status-labels.js";
import { relativeSince } from "../../domain/review/engineering-activity.js";
import { contextStore } from "../auth/context-store.js";
import { platform } from "../../app/browser-platform.js";
import { AVR_STATUS_LABELS, type AvrReport } from "../../domain/engineering/avr-report-domain.js";
import { AVR_PLH_QUEUE_STATUSES, avrEngineeringCounts, statusBadgeClass } from "./avr-page-context.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { TECHNICAL_DOCUMENT_TYPE_LABELS, type TechnicalDocumentType } from "../../domain/review/technical-document-review.js";
import { ENGINEERING_PENDING_STATUSES, ENGINEERING_WORKFLOW_STATUS_LABELS, type EngineeringWorkflowStatus } from "../../domain/review/engineering-workflow-status.js";
import { TECHNICAL_CORRECTION_PRIORITY_LABELS, relativeTimeLabel, type TechnicalCorrectionPriority } from "../../domain/review/technical-correction.js";
import { HOLD_CATEGORY_LABELS, HOLD_DOCUMENT_TYPE_LABELS, holdDurationDays, type DocumentHold } from "../../domain/review/document-hold.js";
import type { EngineeringBoard, EngineeringCardKey, EngineeringQueueFilters, EngineeringQueueRow, EngineeringSort } from "../../application/services/engineering-dashboard-service.js";
import { PLH_REVIEW_UI_STYLES } from "../components/plh-review-ui.js";
import { HOLD_UI_STYLES } from "../components/hold-ui.js";
import { CORRECTION_UI_STYLES } from "../components/correction-request-ui.js";

type PanelTab = "painel" | "analise" | "revisao" | "correcoes" | "hold" | "finalizados" | "historico" | "avr";

/**
 * As seis abas oficiais do Painel (Bloco 2), mais HOLD e AVR, que já existiam
 * e não são removidas.
 *
 * FINALIZADOS e HISTÓRICO eram uma aba só ("APROVADOS / HISTÓRICO"), e por
 * isso o que está tecnicamente pronto ficava misturado com HOLDs liberados e
 * com a lista completa de RI. São perguntas diferentes: "o que fechou?" e "o
 * que já passou por aqui?".
 */
const TABS: Array<[PanelTab, string]> = [
  ["painel", "VISÃO GERAL"],
  ["analise", "ANÁLISE"],
  ["revisao", "REVISÃO E APROVAÇÃO"],
  ["correcoes", "CORREÇÕES"],
  ["hold", "RELATÓRIOS EM HOLD"],
  ["finalizados", "FINALIZADOS"],
  ["historico", "HISTÓRICO"],
  ["avr", "AVR"]
];

/**
 * Aliases de URL. `aprovados` continua respondendo — era a aba anterior e
 * pode estar em link salvo ou favorito; passa a cair em FINALIZADOS.
 */
const TAB_ALIASES: Record<string, PanelTab> = {
  review: "revisao", corrections: "correcoes",
  approved: "finalizados", aprovados: "finalizados", finalized: "finalizados",
  history: "historico",
  minutes: "painel", minutas: "painel", analysis: "analise"
};

/**
 * ANÁLISE × REVISÃO — uma etapa documental aparece em UMA fila só.
 *
 * O workflow central não distingue "análise técnica" de "revisão/aprovação":
 * os dois colapsam em AGUARDANDO_ANALISE_PLH / EM_ANALISE_PLH /
 * REENVIADO_AO_PLH. O recorte é feito aqui, por TIPO de documento, sem criar
 * status novo (e sem duplicar o mesmo documento em duas abas):
 *
 *   REVISÃO  → Relatório de Medição de Espessura (ME), que o elaborador
 *              libera para revisão e aprovação documental do PLH;
 *   ANÁLISE  → os demais documentos enviados para análise técnica do PLH.
 */
const REVIEW_TAB_DOCUMENT_TYPES: ReadonlySet<string> = new Set(["ME"]);

/** Rótulos dos cards, reaproveitados pelo recorte aberto na Visão Geral. */
const ENGINEERING_CARD_LABELS: Partial<Record<EngineeringCardKey, string>> = {
  awaitingAnalysis: "Aguardando análise",
  inAnalysis: "Em análise",
  avrForAnalysis: "AVR para análise",
  pendingCorrections: "Correções pendentes",
  resubmitted: "Reenviados",
  onHold: "Relatórios em HOLD",
  minutaPending: "Minuta técnica pendente",
  awaitingFinalization: "Aguardando finalização",
  finalized: "Finalizados"
};

/**
 * Períodos do cabeçalho. `0` é "todo o histórico" — a opção precisa existir
 * de forma explícita, senão um recorte padrão esconderia documentos antigos
 * ainda pendentes sem que ninguém percebesse que havia um filtro ativo.
 */
const PERIOD_OPTIONS: Array<[string, string]> = [
  ["30", "Período: últimos 30 dias"],
  ["7", "Período: últimos 7 dias"],
  ["90", "Período: últimos 90 dias"],
  ["365", "Período: últimos 12 meses"],
  ["0", "Período: todo o histórico"]
];

/** Rótulo exigido pela fila de ANÁLISE — o status de domínio não é renomeado. */
const AWAITING_ANALYSIS_LABEL = "AGUARDANDO PARA ANÁLISE";
/** Rótulo da fila de REVISÃO para o documento que aguarda o PLH. */
const AWAITING_REVIEW_LABEL = "AGUARDANDO REVISÃO DO PLH";

/**
 * PAINEL DE ENGENHARIA — central de gestão técnica: cards que filtram,
 * MINHAS PENDÊNCIAS, fila priorizada (crítica → mais antigos) com ANALISAR,
 * filtros completos, Minutas, correções pendentes, HOLD (analisar/comentar) e
 * histórico. Tudo vem do EngineeringDashboardService (somente leitura); as
 * ações abrem o workspace certo de cada documento.
 */
export default class EngineeringPage extends HTMLElement {
  private tab: PanelTab = "painel";
  private board: EngineeringBoard | null = null;
  private loading = true;
  private error: string | null = null;
  private filters: EngineeringQueueFilters = { documentType: "ALL", status: "ALL", priority: "ALL", sort: "PRIORITY", card: null };
  private holdModal: { open: boolean; hold: DocumentHold | null; comment: string; error: string; busy: boolean } = { open: false, hold: null, comment: "", error: "", busy: false };
  private holdHistory: DocumentHold[] = [];
  private riReports: readonly any[] = [];
  private clients: any[] = [];
  /** AVRs do contexto — seção própria, sem misturar com RI/Minuta. */
  private avrs: readonly AvrReport[] = [];
  private avrFilter: "QUEUE" | "ALL" | "ELABORACAO" | "AGUARDANDO" | "CORRECAO" | "APROVADA" = "QUEUE";
  /**
   * Período do cabeçalho, em dias. Abre em "todo o histórico": um recorte
   * padrão de 30 dias esconderia o documento parado há 40 dias, que é
   * exatamente o que o Painel existe para mostrar.
   */
  private period = "0";
  private readonly onClientChanged = () => { void this.loadData(); };

  public connectedCallback(): void {
    const params = new URLSearchParams(location.search);
    const requested = (params.get("tab") || "").toLowerCase();
    if (requested) this.tab = (TABS.some(([id]) => id === requested) ? requested : TAB_ALIASES[requested] || "painel") as PanelTab;
    const card = params.get("card") as EngineeringCardKey | null;
    if (card) this.filters.card = card;
    if (requested === "minutas" || requested === "minutes") this.filters.card = "minutaPending";
    void this.loadData();
    window.addEventListener("active-client-changed", this.onClientChanged);
  }

  public disconnectedCallback(): void {
    window.removeEventListener("active-client-changed", this.onClientChanged);
  }

  private ctx(): any {
    const c = contextStore.get();
    return { activeClientId: c.activeClientId || "", permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId ?? null };
  }

  private async loadData(): Promise<void> {
    this.loading = true;
    this.render();
    try {
      const ctx = this.ctx();
      const has = (p: string) => contextStore.hasPermission(p);
      const [board, clients, avrs, ri, holdHistory] = await Promise.all([
        has(PermissionCode.EngineeringDashboardView) ? platform.engineeringDashboardService.loadBoard(ctx, this.filters) : Promise.resolve(null),
        platform.adminService.listClients(ctx).catch(() => []),
        has(PermissionCode.AvrRead) ? platform.avrReportService.listForEngineering(ctx, ctx.activeClientId || null).catch(() => []) : Promise.resolve([]),
        platform.reportService.listReports(ctx).catch(() => []),
        has(PermissionCode.EngineeringHoldView) ? platform.documentHoldService.listHolds(ctx, { status: "LIBERADO" }).catch(() => []) : Promise.resolve([])
      ]);
      this.board = board;
      this.clients = clients || [];
      this.avrs = avrs || [];
      this.riReports = ri || [];
      this.holdHistory = holdHistory;
      this.error = null;
    } catch (e: any) {
      console.error("Failed to load engineering panel", e);
      this.error = e?.safeMessage || e?.message || "Erro ao carregar o Painel de Engenharia.";
    }
    this.loading = false;
    this.render();
  }

  /** Só refiltra/reordena localmente — a fila já está carregada. */
  private applyFilters(): void {
    if (!this.board) return;
    const svc = platform.engineeringDashboardService;
    const allRows = Array.isArray(this.board.all) ? this.board.all : [];
    this.board = { ...this.board, rows: svc.sort(svc.filter(allRows, this.filters), this.filters.sort || "PRIORITY") };
    this.render();
  }

  private esc(value: unknown): string {
    return String(value ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));
  }

  private when(iso: string | null | undefined): string {
    return iso ? new Date(iso).toLocaleString("pt-BR") : "—";
  }

  private setTab(tab: PanelTab): void {
    this.tab = tab;
    const url = new URL(location.href);
    url.searchParams.set("tab", tab);
    history.replaceState(null, "", url.toString());
    this.render();
  }

  // ------------------------------------------------------------- RENDER

  private render(): void {
    const has = (p: string) => contextStore.hasPermission(p);
    if (!has(PermissionCode.EngineeringDashboardView) && !has(PermissionCode.TechnicalReviewViewQueue) && !has(PermissionCode.AvrRead)) {
      this.innerHTML = `<style>${this.styles()}</style><div class="eng-page"><div class="eng-empty">Você não possui permissão para visualizar o Painel de Engenharia.</div></div>`;
      return;
    }
    const b = this.board;
    this.innerHTML = `
      <style>${this.styles()}</style>
      <div class="eng-page">
        <header class="eng-header">
          <div>
            <h1>Painel de Engenharia</h1>
            <p>Central de gestão técnica: revisão PLH, Minutas, correções por item, HOLD, AVR e histórico — o que está pendente, com quem, por quê e qual ação tomar.</p>
          </div>
          <div class="eng-header-actions">
            <!-- Período: recorta a fila inteira pela data de envio ao PLH.
                 Estava só dentro dos filtros da fila, onde ninguém o via na
                 Visão Geral — e o card FINALIZADOS diz "no período" sem que
                 houvesse período algum à vista. -->
            <label class="eng-period">
              ${this.icon("calendar")}
              <select id="flt-period">
                ${PERIOD_OPTIONS.map(([value, label]) => `<option value="${value}" ${this.period === value ? "selected" : ""}>${this.esc(label)}</option>`).join("")}
              </select>
            </label>
            <button class="eng-btn" id="btn-refresh">${this.icon("refresh")} Atualizar</button>
            ${has(PermissionCode.CalculationAccess) ? `<button class="eng-btn" id="btn-calcs">${this.icon("calc")} Cálculos</button>` : ""}
          </div>
        </header>
        <nav class="eng-tabs">
          ${TABS.filter(([id]) => id !== "avr" || has(PermissionCode.AvrRead)).map(([id, label]) => `<button class="eng-tab ${this.tab === id ? "active" : ""}" data-tab="${id}">${label}${this.tabBadge(id)}</button>`).join("")}
        </nav>
        ${this.error ? `<div class="eng-notice eng-notice-error">${this.esc(this.error)}</div>` : ""}
        ${this.loading && !b ? `<div class="eng-empty">Carregando o painel…</div>` : ""}
        ${b ? this.renderTab(b) : (this.tab === "avr" ? this.renderAvrSection() : "")}
        ${this.holdModal.open ? this.renderHoldModal() : ""}
      </div>`;
    this.attachEvents();
  }

  private tabBadge(id: PanelTab): string {
    const b = this.board;
    if (!b) return "";
    // Cada pendente é contado numa aba só — antes "painel" e "revisao"
    // somavam o mesmo documento, reforçando a duplicação visual.
    const n = id === "analise" ? this.pendingRowsFor(b, "analise").length
      : id === "revisao" ? this.pendingRowsFor(b, "revisao").length
      : id === "correcoes" ? b.corrections.length
      : id === "hold" ? b.counts.onHold
      : id === "avr" ? avrEngineeringCounts(this.avrs).pendentesPlh : 0;
    return n > 0 ? ` <span class="eng-tab-badge">${n}</span>` : "";
  }

  /**
   * Fila pendente do PLH separada por destino. Um documento entra em exatamente
   * uma das duas listas — nunca nas duas (comando, itens 7/33).
   */
  private pendingRowsFor(b: EngineeringBoard, destination: "analise" | "revisao"): EngineeringBoard["rows"] {
    return b.rows.filter(row => {
      if (!ENGINEERING_PENDING_STATUSES.includes(row.workflowStatus as never)) return false;
      const isReview = REVIEW_TAB_DOCUMENT_TYPES.has(String(row.documentType));
      return destination === "revisao" ? isReview : !isReview;
    });
  }

  private renderTab(b: EngineeringBoard): string {
    switch (this.tab) {
      // O PAINEL é a visão geral (cards + minhas pendências). A fila completa
      // vive em ANÁLISE e em REVISÃO — antes ela era repetida aqui e na aba
      // de revisão, fazendo o mesmo documento aparecer duas vezes.
      /**
       * VISÃO GERAL, de cima para baixo: panorama → trabalho → ação.
       * A fila só aparece aqui quando um card está ativo — é o que dá
       * sentido ao clique no card, que antes filtrava algo invisível
       * nesta aba.
       */
      case "painel": return `
        ${this.renderCards(b)}
        ${this.filters.card ? this.renderCardQueue(b) : ""}
        <div class="eng-grid-main">
          ${this.renderMyPendencies(b)}
          ${this.renderCorrectionSummary(b)}
        </div>
        <div class="eng-grid-main">
          ${this.renderActivity(b)}
          ${this.renderQuickActions()}
        </div>`;
      case "analise": return this.renderPendingQueue(b, "analise");
      case "revisao": return this.renderPendingQueue(b, "revisao");
      case "correcoes": return this.renderCorrections(b);
      case "hold": return this.renderHolds(b);
      case "finalizados": return this.renderFinalized(b);
      case "historico": return this.renderHistory(b);
      case "avr": return this.renderAvrSection();
    }
  }

  // ----------------------------------------------------------- DASHBOARD

  /**
   * CARDS DE STATUS — número, título, subtítulo, ícone e indicação de
   * clique. Sem gráfico interno e sem cor saturada: cada card ganha só um
   * acento neutro que o diferencia, porque nove cards com nove cores fortes
   * competem entre si e nenhum se destaca.
   *
   * Cada card continua sendo um FILTRO: ao ativá-lo, a fila correspondente
   * abre logo abaixo, na mesma tela. Antes o clique filtrava algo que a
   * Visão Geral não mostrava, e a seta não levava a lugar nenhum.
   */
  private renderCards(b: EngineeringBoard): string {
    const c = b.counts;
    const card = (key: EngineeringCardKey, title: string, count: number, sub: string, icon: string, accent: string) => `
      <button class="eng-card accent-${accent} ${this.filters.card === key ? "active" : ""}" data-card="${key}" aria-pressed="${this.filters.card === key}">
        <span class="eng-card-rail"></span>
        <span class="eng-card-ico">${this.icon(icon)}</span>
        <span class="eng-card-body">
          <span class="eng-card-value">${count}</span>
          <span class="eng-card-title">${this.esc(title)}</span>
          <span class="eng-card-sub">${this.esc(sub)}</span>
        </span>
        <span class="eng-card-go" aria-hidden="true">›</span>
      </button>`;
    return `
      <div class="eng-cards">
        ${card("awaitingAnalysis", "Aguardando análise", c.awaitingAnalysis, "Relatórios na fila", "doc", "neutral")}
        ${card("inAnalysis", "Em análise", c.inAnalysis, "Em revisão técnica", "clock", "neutral")}
        ${card("avrForAnalysis", "AVR para análise", c.avrForAnalysis, "Aguardando avaliação", "doc", "info")}
        ${card("pendingCorrections", "Correções pendentes", c.pendingCorrections, "Ações a serem realizadas", "alert", "warn")}
        ${card("resubmitted", "Reenviados", c.resubmitted, "Aguardando nova análise", "people", "info")}
        ${card("onHold", "Relatórios em HOLD", c.onHold, "Aguardando liberação", "pause", "sand")}
        ${card("minutaPending", "Minuta técnica pendente", c.minutaPending, "Minutas para revisão", "doc", "sand")}
        ${card("awaitingFinalization", "Aguardando finalização", c.awaitingFinalization, "Em etapa final", "hourglass", "neutral")}
        ${card("finalized", "Finalizados", c.finalized, "No período", "check", "done")}
      </div>`;
  }

  /**
   * MINHAS PENDÊNCIAS — chips de recorte rápido MAIS a tabela do que de
   * fato está na mão da pessoa. Antes havia só os chips: eles diziam
   * "2 aguardando minha análise" e obrigavam a trocar de aba para descobrir
   * QUAIS eram. Ver o item, o prazo e a ação sem sair da Visão Geral é o
   * ganho operacional do bloco.
   */
  private renderMyPendencies(b: EngineeringBoard): string {
    const m = b.mine;
    const me = contextStore.get().professionalId;
    const chip = (n: number, label: string, key: EngineeringCardKey | null, tab: PanelTab | null) => `
      <button class="eng-chip ${key && this.filters.card === key ? "active" : ""}" data-mine-card="${key || ""}" data-mine-tab="${tab || ""}">
        ${this.esc(label)}<span class="eng-chip-n">${n}</span>
      </button>`;
    const rows = this.myPendencyRows(b);
    return `
      <section class="eng-block">
        <div class="eng-block-head">
          <div>
            <h2>${this.icon("people")} Minhas Pendências</h2>
            <small>${me ? "Itens que requerem a sua ação." : "Sem Profissional vinculado à sessão — mostrando o que está pendente no contexto do Cliente."}</small>
          </div>
          <button class="eng-link" data-mine-tab="analise">Ver todas →</button>
        </div>
        <div class="eng-chips">
          ${chip(rows.length, "Todas", null, null)}
          ${chip(m.awaitingMyAnalysis, "Aguardando minha análise", "awaitingAnalysis", null)}
          ${chip(m.resubmittedToMe, "Correções reenviadas", "resubmitted", null)}
          ${chip(m.onHold, "Em HOLD", "onHold", null)}
          ${chip(b.counts.minutaPending, "Minuta técnica pendente", "minutaPending", null)}
          ${chip(m.correctionsRequestedByMe, "Correções que solicitei", null, "correcoes")}
        </div>
        ${this.renderMyPendencyTable(rows)}
      </section>`;
  }

  /**
   * As linhas que a pessoa precisa resolver. Com Profissional na sessão, as
   * dela (`mine`); sem Profissional, as pendentes do contexto — dizer
   * "nenhuma pendência" a quem não tem Professional vinculado esconderia a
   * fila inteira do Cliente.
   */
  private myPendencyRows(b: EngineeringBoard): EngineeringQueueRow[] {
    const hasProfessional = !!contextStore.get().professionalId;
    const source = hasProfessional ? b.rows.filter(r => r.mine || r.onHold) : b.rows;
    return source
      .filter(r => ENGINEERING_PENDING_STATUSES.includes(r.workflowStatus as never) || r.onHold)
      .slice(0, 8);
  }

  private renderMyPendencyTable(rows: EngineeringQueueRow[]): string {
    if (rows.length === 0) {
      return `<p class="eng-empty-block">Nenhuma pendência para você neste contexto. Os cards acima mostram o que está em andamento com outras pessoas.</p>`;
    }
    return `
      <div class="eng-table-wrap">
        <table class="eng-table eng-table-compact">
          <thead><tr>
            <th>Item / TAG</th><th>Tipo</th><th>Projeto</th><th>Aguardando</th><th>Status</th><th>Responsável</th><th style="text-align:right;">Ações</th>
          </tr></thead>
          <tbody>
            ${rows.map(r => `
              <tr>
                <td><strong>${this.esc(r.tag || r.documentNumber || "—")}</strong>${r.tag && r.documentNumber ? `<br><small class="eng-muted">${this.esc(r.documentNumber)}</small>` : ""}</td>
                <td>${this.esc(r.typeLabel)}</td>
                <td>${this.esc(r.project || "—")}</td>
                <td class="nowrap" title="${this.esc(this.when(r.submittedAt))}">${this.esc(r.waitingLabel)}</td>
                <td><span class="eng-status ws-${r.workflowStatus.toLowerCase()}">${this.esc(r.workflowLabel)}</span></td>
                <td>${this.esc(r.plhName || "—")}${r.mine ? ` <span class="eng-you">VOCÊ</span>` : ""}</td>
                <td class="nowrap" style="text-align:right;">
                  <button class="eng-btn primary btn-analyze" data-route="${this.esc(r.workflowStatus === "MINUTA_PENDENTE" ? `${r.reviewRoute}#minuta` : r.reviewRoute)}">Analisar</button>
                  ${r.holdId ? `<button class="eng-btn btn-hold-analyze" data-hold="${this.esc(r.holdId)}">HOLD</button>` : ""}
                </td>
              </tr>`).join("")}
          </tbody>
        </table>
      </div>`;
  }

  /**
   * CORREÇÕES / REVISÕES — pelos estados que EXISTEM no domínio.
   *
   * O item de correção tem três: PENDENTE, CORRIGIDA e CANCELADA. Não
   * existe "correção em análise" nem "correção aprovada/rejeitada" — quem é
   * analisado e aprovado é o DOCUMENTO, depois de as correções serem
   * executadas. A quarta linha é, por isso, DOCUMENTOS que voltaram ao PLH,
   * rotulada como tal. Inventar um quarto estado de item só para a lista
   * ficar simétrica faria o Painel afirmar o que o sistema não sustenta.
   */
  private renderCorrectionSummary(b: EngineeringBoard): string {
    const s = b.correctionSummary;
    const line = (n: number, title: string, sub: string, icon: string, accent: string, tab: PanelTab, card: EngineeringCardKey | null) => `
      <button class="eng-sum-row" data-mine-tab="${tab}" data-mine-card="${card || ""}">
        <span class="eng-sum-n accent-${accent}">${n}</span>
        <span class="eng-sum-ico">${this.icon(icon)}</span>
        <span class="eng-sum-text"><strong>${this.esc(title)}</strong><small>${this.esc(sub)}</small></span>
        <span class="eng-sum-go" aria-hidden="true">›</span>
      </button>`;
    return `
      <section class="eng-block eng-block-side">
        <div class="eng-block-head">
          <div>
            <h2>${this.icon("refresh")} Correções / Revisões</h2>
            <small>Estados reais dos itens de correção.</small>
          </div>
          <button class="eng-link" data-mine-tab="correcoes">Ver todas →</button>
        </div>
        <div class="eng-sum">
          ${line(s.pending, "Correções pendentes", "Itens a executar no documento", "alert", "warn", "correcoes", "pendingCorrections")}
          ${line(s.awaitingReanalysis, "Documentos reenviados", "Corrigidos, aguardando o PLH", "doc", "info", "analise", "resubmitted")}
          ${line(s.resolved, "Correções executadas", "Itens já corrigidos", "check", "done", "correcoes", null)}
          ${line(s.cancelled, "Correções canceladas", "Retiradas por quem pediu", "close", "neutral", "correcoes", null)}
        </div>
        ${s.total === 0 ? `<p class="eng-empty-block">Nenhum item de correção registrado neste contexto.</p>` : ""}
      </section>`;
  }

  /**
   * ATIVIDADE RECENTE — eventos REAIS.
   *
   * A fonte é o `history` das revisões somado aos pedidos de correção, não
   * a trilha de auditoria: no app real ela é descartada (`mockAudit`,
   * CLAUDE.md §17) e um feed montado sobre ela viria sempre vazio. Sem
   * movimento no contexto, a lista diz isso — não mostra exemplos.
   */
  private renderActivity(b: EngineeringBoard): string {
    const now = new Date();
    return `
      <section class="eng-block">
        <div class="eng-block-head">
          <div>
            <h2>${this.icon("clock")} Atividade recente</h2>
            <small>Últimas atualizações na engenharia.</small>
          </div>
        </div>
        ${b.activity.length === 0
          ? `<p class="eng-empty-block">Nenhuma movimentação registrada neste contexto ainda. A lista mostra envios, análises, correções e aprovações conforme acontecem.</p>`
          : `<ul class="eng-feed">
              ${b.activity.map(entry => `
                <li${entry.route ? ` class="is-clickable" data-activity-route="${this.esc(entry.route)}"` : ""}>
                  <span class="eng-feed-ico">${this.icon(this.activityIcon(entry.action))}</span>
                  <span class="eng-feed-text">
                    <strong>${this.esc(entry.actorName)}</strong> — ${this.esc(entry.action)} · <strong>${this.esc(entry.itemLabel)}</strong>
                    ${entry.detail ? `<small>${this.esc(entry.detail)}</small>` : ""}
                  </span>
                  <span class="eng-feed-when">${this.esc(relativeSince(entry.at, now))}</span>
                </li>`).join("")}
             </ul>`}
      </section>`;
  }

  private activityIcon(action: string): string {
    const a = action.toLowerCase();
    if (a.includes("cancel")) return "close";
    if (a.includes("aprovad") || a.includes("assinad") || a.includes("finaliz") || a.includes("executad")) return "check";
    if (a.includes("correção")) return "alert";
    if (a.includes("reenviado") || a.includes("enviado")) return "send";
    return "clock";
  }

  /**
   * AÇÕES RÁPIDAS — rotinas que existem de verdade e que a pessoa pode
   * executar. Cada uma só aparece se a permissão correspondente existir:
   * oferecer um atalho que será negado ao clicar é pior que não oferecer.
   */
  private renderQuickActions(): string {
    const has = (p: string) => contextStore.hasPermission(p);
    const actions: Array<{ label: string; icon: string; tab?: PanelTab; route?: string }> = [];
    if (has(PermissionCode.EngineeringHoldView)) actions.push({ label: "Ver itens em HOLD", icon: "pause", tab: "hold" });
    if (has(PermissionCode.TechnicalReviewViewQueue)) actions.push({ label: "Fila de análise técnica", icon: "doc", tab: "analise" });
    if (has(PermissionCode.TechnicalReviewViewQueue)) actions.push({ label: "Revisão e aprovação", icon: "check", tab: "revisao" });
    actions.push({ label: "Correções por item", icon: "alert", tab: "correcoes" });
    if (has(PermissionCode.CalculationAccess)) actions.push({ label: "Cálculos de engenharia", icon: "calc", route: "/engineering/calculations" });
    if (has(PermissionCode.AvrRead)) actions.push({ label: "Avaliações de Vida Residual", icon: "hourglass", tab: "avr" });

    return `
      <section class="eng-block eng-block-side">
        <div class="eng-block-head">
          <div>
            <h2>${this.icon("bolt")} Ações rápidas</h2>
            <small>Acesse as principais rotinas.</small>
          </div>
        </div>
        ${actions.length === 0
          ? `<p class="eng-empty-block">Seu perfil não possui acesso às rotinas da engenharia.</p>`
          : `<div class="eng-quick">
              ${actions.map(a => `
                <button class="eng-quick-item" ${a.tab ? `data-mine-tab="${a.tab}"` : ""} ${a.route ? `data-route="${this.esc(a.route)}" ` : ""}>
                  <span class="eng-quick-ico">${this.icon(a.icon)}</span>
                  <span>${this.esc(a.label)}</span>
                  <span class="eng-quick-go" aria-hidden="true">→</span>
                </button>`).join("")}
             </div>`}
      </section>`;
  }

  /** Ícones de traço, neutros — sem preenchimento e sem cor própria. */
  private icon(name: string): string {
    const paths: Record<string, string> = {
      doc: `<path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z"/><path d="M14 2v5h5"/>`,
      clock: `<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>`,
      alert: `<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/>`,
      people: `<path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9.5" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.9"/>`,
      pause: `<circle cx="12" cy="12" r="9"/><path d="M10 9v6M14 9v6"/>`,
      hourglass: `<path d="M6 2h12M6 22h12"/><path d="M6 2c0 5 6 6 6 10s-6 5-6 10"/><path d="M18 2c0 5-6 6-6 10s6 5 6 10"/>`,
      check: `<circle cx="12" cy="12" r="9"/><path d="m8.5 12.5 2.5 2.5 4.5-5"/>`,
      close: `<circle cx="12" cy="12" r="9"/><path d="m9 9 6 6M15 9l-6 6"/>`,
      refresh: `<path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/>`,
      send: `<path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4z"/>`,
      bolt: `<path d="M13 2 3 14h8l-1 8 10-12h-8z"/>`,
      calc: `<rect x="4" y="2" width="16" height="20" rx="2"/><path d="M8 6h8M8 11h2M12 11h2M16 11h0M8 15h2M12 15h2M16 15h0M8 19h8"/>`,
      calendar: `<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>`
    };
    return `<svg class="eng-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths[name] || paths["doc"]}</svg>`;
  }

  // ---------------------------------------------------------------- QUEUE

  /** Fila única de cada destino, com o rótulo de status exigido pelo comando. */
  private renderPendingQueue(b: EngineeringBoard, destination: "analise" | "revisao"): string {
    const rows = this.pendingRowsFor(b, destination);
    const statusLabel = destination === "analise" ? AWAITING_ANALYSIS_LABEL : AWAITING_REVIEW_LABEL;
    const hint = destination === "analise"
      ? `Documentos enviados para ANÁLISE TÉCNICA do PLH — status ${AWAITING_ANALYSIS_LABEL}. Fila única: a mesma situação não aparece em outra aba.`
      : `Relatórios de Medição de Espessura (ME) liberados para REVISÃO E APROVAÇÃO do PLH — status ${AWAITING_REVIEW_LABEL}.`;
    return `
      <section class="eng-section">
        ${this.renderQueueFilters(b, false)}
        <div class="eng-queue-status-note"><strong>${statusLabel}</strong> · ${rows.length} documento(s)</div>
        ${this.renderQueueTable(rows, hint)}
      </section>`;
  }

  /** A fila do card ativo, aberta na própria Visão Geral, com como sair dela. */
  private renderCardQueue(b: EngineeringBoard): string {
    const key = this.filters.card as EngineeringCardKey;
    const label = ENGINEERING_CARD_LABELS[key] ?? key;
    return `
      <section class="eng-block">
        <div class="eng-block-head">
          <div><h2>${this.esc(label)}</h2><small>${b.rows.length} documento(s) neste recorte.</small></div>
          <button class="eng-link" id="btn-clear-card">Limpar recorte ✕</button>
        </div>
        ${key === "onHold" ? this.renderHoldTable(b.holds, "Documentos em HOLD.") : this.renderQueueTable(b.rows, `Documentos do card ${label}.`)}
      </section>`;
  }

  private renderQueueSection(b: EngineeringBoard, compact: boolean): string {
    const f = this.filters;
    if (f.card === "onHold") return `<section class="eng-section">${this.renderQueueFilters(b, compact)}${this.renderHoldTable(b.holds, "Documentos em HOLD (card RELATÓRIOS EM HOLD).")}</section>`;
    if (f.card === "minutaPending") return `<section class="eng-section">${this.renderQueueFilters(b, compact)}${this.renderQueueTable(b.rows, "Relatórios de Integridade validados cuja Minuta Técnica ainda não foi validada — ANALISAR abre a Minuta Técnica.")}</section>`;
    return `<section class="eng-section">${this.renderQueueFilters(b, compact)}${this.renderQueueTable(b.rows, compact ? "Fila priorizada — CRÍTICA primeiro, depois os mais antigos." : "Todos os documentos enviados ao PLH (LP, ME, PM, US-Convencional, Integridade).")}</section>`;
  }

  private renderQueueFilters(b: EngineeringBoard, compact: boolean): string {
    const f = this.filters;
    const sel = (id: string, label: string, options: Array<[string, string]>, current: string | undefined) => `
      <label class="eng-field">${label}<select id="${id}">${options.map(([v, l]) => `<option value="${this.esc(v)}" ${(current || "") === v ? "selected" : ""}>${this.esc(l)}</option>`).join("")}</select></label>`;
    const list = (values: string[], all = "Todos") => [["", all], ...values.map(v => [v, v] as [string, string])] as Array<[string, string]>;
    const types: Array<[string, string]> = [["ALL", "Todos"], ...(["INTEGRITY", "ME", "LP", "PM", "BSCAN", "USC", "AVR"] as TechnicalDocumentType[]).map(t => [t, TECHNICAL_DOCUMENT_TYPE_LABELS[t]] as [string, string])];
    const statuses: Array<[string, string]> = [["ALL", "Todos os status"], ...(Object.keys(ENGINEERING_WORKFLOW_STATUS_LABELS) as EngineeringWorkflowStatus[]).map(s => [s, ENGINEERING_WORKFLOW_STATUS_LABELS[s]] as [string, string])];
    const priorities: Array<[string, string]> = [["ALL", "Todas"], ...(Object.keys(TECHNICAL_CORRECTION_PRIORITY_LABELS) as TechnicalCorrectionPriority[]).map(p => [p, TECHNICAL_CORRECTION_PRIORITY_LABELS[p]] as [string, string])];
    const sorts: Array<[string, string]> = [["PRIORITY", "Prioridade crítica → mais antigos"], ["OLDEST", "Mais antigos primeiro"], ["NEWEST", "Mais recentes primeiro"]];
    return `
      <div class="eng-section-head">
        <h2>${compact ? "FILA PRIORIZADA" : "REVISÃO DE RELATÓRIOS"}</h2>
        <div class="eng-inline">
          ${f.card ? `<span class="eng-chip">Filtro do card: ${this.esc(this.cardLabel(f.card))} <button class="eng-chip-x" id="btn-clear-card">✕</button></span>` : ""}
          ${sel("flt-sort", "Ordenar", sorts, f.sort || "PRIORITY")}
        </div>
      </div>
      <div class="eng-filters ${compact ? "compact" : ""}">
        ${sel("flt-type", "Tipo de documento", types, f.documentType || "ALL")}
        ${sel("flt-client", "Cliente", list(b.options.clients), f.client)}
        ${sel("flt-project", "Projeto", list(b.options.projects), f.project)}
        ${sel("flt-unit", "Unidade / Área", list(b.options.unitAreas, "Todas"), f.unitArea)}
        <label class="eng-field">TAG<input id="flt-tag" list="eng-tags" value="${this.esc(f.tag || "")}" placeholder="TK-101"><datalist id="eng-tags">${b.options.tags.map(t => `<option value="${this.esc(t)}">`).join("")}</datalist></label>
        ${sel("flt-os", "OS", list(b.options.osNumbers, "Todas"), f.osNumber)}
        ${sel("flt-subos", "Sub-OS", list(b.options.subOsNumbers, "Todas"), f.subOsNumber)}
        ${sel("flt-inspector", "Inspetor", list(b.options.inspectors), f.inspector)}
        ${sel("flt-plh", "Engenheiro / PLH", [["", "Todos"], ...b.options.plhs.map(p => [p.id, p.name] as [string, string])], f.plhProfessionalId)}
        ${sel("flt-status", "Status", statuses, f.status || "ALL")}
        <label class="eng-field">Período — de<input type="date" id="flt-from" value="${this.esc(f.from || "")}"></label>
        <label class="eng-field">até<input type="date" id="flt-to" value="${this.esc(f.to || "")}"></label>
        ${sel("flt-priority", "Prioridade", priorities, f.priority || "ALL")}
        <label class="eng-field eng-field-wide">Busca geral<input type="search" id="flt-search" value="${this.esc(f.search || "")}" placeholder="Nº relatório, TAG, OS, Sub-OS, cliente, projeto, inspetor, PLH…"></label>
        <button class="eng-btn" id="btn-clear-filters">Limpar filtros</button>
      </div>`;
  }

  private cardLabel(key: EngineeringCardKey): string {
    return ({ awaitingAnalysis: "Aguardando análise", inAnalysis: "Em análise", avrForAnalysis: "AVR para análise", pendingCorrections: "Correções pendentes", awaitingCorrection: "Aguardando correção", resubmitted: "Reenviados", onHold: "Relatórios em HOLD", minutaPending: "Minuta Técnica pendente", minutasInProgress: "Minutas em elaboração", awaitingApproval: "Aguardando aprovação", approved: "Aprovados", awaitingFinalization: "Aguardando finalização", finalized: "Finalizados" } as Record<EngineeringCardKey, string>)[key];
  }

  private renderQueueTable(rows: EngineeringQueueRow[], caption: string): string {
    const safeRows = Array.isArray(rows) ? rows : [];
    const heads = ["Prior.", "Tipo", "Nº", "Rev.", "Cliente", "Projeto", "Unidade", "TAG", "OS / Sub-OS", "Inspetor", "Engenheiro responsável", "PLH responsável", "Data", "Status", "Tempo pendente", "Ação"];
    return `
      <p class="eng-caption">${this.esc(caption)}</p>
      <div class="eng-table-wrap">
        <table class="eng-table">
          <thead><tr>${heads.map((h, i) => `<th ${i === heads.length - 1 ? 'style="text-align:right;"' : ""}>${h.toUpperCase()}</th>`).join("")}</tr></thead>
          <tbody>
            ${safeRows.length === 0 ? `<tr><td colspan="${heads.length}" class="eng-empty-row">Nenhum documento para os filtros selecionados.</td></tr>` : safeRows.map(r => `
              <tr class="${r.mine ? "mine" : ""} ${r.onHold ? "onhold" : ""}">
                <td>${r.priority ? `<span class="eng-prio prio-${r.priority === "CRÍTICA" ? "critica" : r.priority === "ALTA" ? "alta" : "normal"}" title="${r.pendingCorrections} correção(ões) pendente(s)">${this.esc(r.priority)}</span>` : `<span class="eng-muted">—</span>`}</td>
                <td><strong>${this.esc(r.typeLabel)}</strong></td>
                <td><strong>${this.esc(r.documentNumber)}</strong>${r.cycle > 1 ? `<br><small class="eng-muted">ciclo ${r.cycle}</small>` : ""}</td>
                <td>${this.esc(r.revision)}</td>
                <td>${this.esc(r.client || "—")}</td>
                <td>${this.esc(r.project || "—")}</td>
                <td>${this.esc(r.unitArea || "—")}</td>
                <td><strong>${this.esc(r.tag || "—")}</strong></td>
                <td>${this.esc([r.osNumber, r.subOsNumber].filter(Boolean).join(" / ") || "—")}</td>
                <td>${this.esc(r.inspectorName || r.elaboratedBy || "—")}</td>
                <td>${this.esc(r.engineerName || "—")}</td>
                <td>${this.esc(r.plhName || "—")}${r.mine ? ` <span class="eng-you">VOCÊ</span>` : ""}</td>
                <td class="nowrap">${this.esc(this.when(r.submittedAt))}</td>
                <td><span class="eng-status ws-${r.workflowStatus.toLowerCase()}">${this.esc(r.workflowLabel)}</span>${r.pendingCorrections > 0 ? `<br><small class="eng-muted">${r.pendingCorrections} item(ns) pendente(s)</small>` : ""}</td>
                <td class="nowrap" title="${this.esc(this.when(r.submittedAt))}">${this.esc(r.waitingLabel)}</td>
                <td class="nowrap" style="text-align:right;">
                  <button class="eng-btn primary btn-analyze" data-route="${this.esc(r.workflowStatus === "MINUTA_PENDENTE" ? `${r.reviewRoute}#minuta` : r.reviewRoute)}">ANALISAR</button>
                  <button class="eng-btn btn-analyze" data-route="${this.esc(r.documentRoute)}">Ver relatório</button>
                  ${r.holdId ? `<button class="eng-btn btn-hold-analyze" data-hold="${this.esc(r.holdId)}">ANALISAR HOLD</button>` : ""}
                </td>
              </tr>`).join("")}
          </tbody>
        </table>
      </div>`;
  }

  // ----------------------------------------------------------- CORRECTIONS

  private renderCorrections(b: EngineeringBoard): string {
    const rows = [...b.corrections].sort((x, y) => x.createdAt.localeCompare(y.createdAt));
    return `
      <section class="eng-section">
        <div class="eng-section-head"><h2>CORREÇÕES PENDENTES</h2><small>Itens solicitados pelo PLH/Engenharia ainda não marcados como corrigidos pelo Inspetor.</small></div>
        <div class="eng-table-wrap">
          <table class="eng-table">
            <thead><tr>${["Prior.", "Tipo", "Documento", "Rev.", "Item", "Seção", "Item / Campo", "Observação", "Solicitada por", "Inspetor responsável", "Há", "Ação"].map((h, i) => `<th ${i === 11 ? 'style="text-align:right;"' : ""}>${h.toUpperCase()}</th>`).join("")}</tr></thead>
            <tbody>
              ${rows.length === 0 ? `<tr><td colspan="12" class="eng-empty-row">Nenhuma correção pendente.</td></tr>` : rows.map(c => `
                <tr>
                  <td><span class="eng-prio prio-${c.priority === "CRÍTICA" ? "critica" : c.priority === "ALTA" ? "alta" : "normal"}">${this.esc(c.priority)}</span></td>
                  <td><strong>${this.esc(TECHNICAL_DOCUMENT_TYPE_LABELS[c.documentType])}</strong></td>
                  <td><strong>${this.esc(c.documentNumber)}</strong></td>
                  <td>${this.esc(c.revision)}</td>
                  <td>${this.esc(c.sequence)}</td>
                  <td>${this.esc(c.sectionLabel)}</td>
                  <td>${this.esc(c.itemLabel || "—")}${c.fieldId ? `<br><small class="eng-muted">${this.esc(c.fieldId)}</small>` : ""}</td>
                  <td class="eng-wrap">${this.esc(c.comment)}</td>
                  <td>${this.esc(c.requestedBy.name)}</td>
                  <td>${this.esc(c.assignedTo.name || "—")}</td>
                  <td class="nowrap" title="${this.esc(this.when(c.createdAt))}">${this.esc(relativeTimeLabel(c.createdAt))}</td>
                  <td class="nowrap" style="text-align:right;">
                    <button class="eng-btn btn-analyze" data-route="${this.esc(c.anchor)}">Ir ao item</button>
                    ${c.reviewId ? `<button class="eng-btn primary btn-analyze" data-route="/engineering/review/${this.esc(c.reviewId)}">ANALISAR</button>` : c.documentType === "INTEGRITY" ? `<button class="eng-btn primary btn-analyze" data-route="/engineering/workspace/${this.esc(c.documentId)}">ANALISAR</button>` : ""}
                  </td>
                </tr>`).join("")}
            </tbody>
          </table>
        </div>
      </section>`;
  }

  // ------------------------------------------------------------------ HOLD

  private renderHolds(b: EngineeringBoard): string {
    return `<section class="eng-section"><div class="eng-section-head"><h2>RELATÓRIOS EM HOLD</h2><small>Documentos parados por quem elabora, com motivo. A Engenharia analisa e comenta; o Inspetor é notificado.</small></div>${this.renderHoldTable(b.holds, "")}</section>`;
  }

  private renderHoldTable(holds: DocumentHold[], caption: string): string {
    const rows = [...holds].sort((a, b) => a.placedAt.localeCompare(b.placedAt));
    return `
      ${caption ? `<p class="eng-caption">${this.esc(caption)}</p>` : ""}
      <div class="eng-table-wrap">
        <table class="eng-table">
          <thead><tr>${["Tipo", "Relatório", "Cliente", "Projeto", "TAG", "Inspetor", "Data do HOLD", "Tempo em HOLD", "Motivo", "Status", "Ação"].map((h, i) => `<th ${i === 10 ? 'style="text-align:right;"' : ""}>${h.toUpperCase()}</th>`).join("")}</tr></thead>
          <tbody>
            ${rows.length === 0 ? `<tr><td colspan="11" class="eng-empty-row">Nenhum documento em HOLD.</td></tr>` : rows.map(h => {
              const days = holdDurationDays(h);
              return `
              <tr>
                <td><strong>${this.esc(HOLD_DOCUMENT_TYPE_LABELS[h.documentType])}</strong></td>
                <td><strong>${this.esc(h.summary.documentNumber)}</strong><br><small class="eng-muted">Rev. ${this.esc(h.summary.revision)}</small></td>
                <td>${this.esc(h.summary.client || "—")}</td>
                <td>${this.esc(h.summary.project || "—")}</td>
                <td><strong>${this.esc(h.summary.tag || "—")}</strong></td>
                <td>${this.esc(h.summary.inspectorName || h.placedBy.name)}</td>
                <td class="nowrap">${this.esc(this.when(h.placedAt))}</td>
                <td class="nowrap">${days === 0 ? "hoje" : `${days} dia${days === 1 ? "" : "s"}`}</td>
                <td class="eng-wrap">${this.esc(h.reason)}${h.category ? `<br><small class="eng-muted">${this.esc(HOLD_CATEGORY_LABELS[h.category])}</small>` : ""}${h.engineeringComments.length > 0 ? `<br><small class="eng-muted">${h.engineeringComments.length} comentário(s) da Engenharia</small>` : ""}</td>
                <td><span class="eng-status ${h.status === "EM_HOLD" ? "ws-em_hold" : "ws-em_elaboracao"}">${h.status === "EM_HOLD" ? "Em HOLD" : `Liberado${h.releasedAt ? ` · ${this.esc(this.when(h.releasedAt))}` : ""}`}</span></td>
                <td class="nowrap" style="text-align:right;">
                  ${h.status === "EM_HOLD" ? `<button class="eng-btn primary btn-hold-analyze" data-hold="${this.esc(h.id)}">ANALISAR HOLD</button>` : `<button class="eng-btn btn-hold-analyze" data-hold="${this.esc(h.id)}">Ver HOLD</button>`}
                  <button class="eng-btn btn-analyze" data-route="${this.esc(h.summary.route)}">Abrir documento</button>
                </td>
              </tr>`;
            }).join("")}
          </tbody>
        </table>
      </div>`;
  }

  private renderHoldModal(): string {
    const m = this.holdModal;
    const h = m.hold;
    if (!h) return "";
    const canComment = contextStore.hasPermission(PermissionCode.EngineeringHoldComment) && h.status === "EM_HOLD";
    return `
      <div class="plh-modal-backdrop">
        <div class="plh-modal" style="max-width:820px;">
          <div class="plh-modal-head"><span>ANALISAR HOLD — ${this.esc(HOLD_DOCUMENT_TYPE_LABELS[h.documentType])} ${this.esc(h.summary.documentNumber)}</span><button class="plh-icon-btn" id="btn-close-hold">✕</button></div>
          <div class="plh-modal-body">
            <div class="plh-grid">
              ${[["Documento", `${h.summary.documentNumber} · Rev. ${h.summary.revision}`], ["Cliente", h.summary.client], ["Projeto", h.summary.project], ["Unidade / Área", h.summary.unitArea], ["TAG", h.summary.tag], ["OS / Sub-OS", [h.summary.osNumber, h.summary.subOsNumber].filter(Boolean).join(" / ")], ["Inspetor responsável", h.summary.inspectorName || h.placedBy.name], ["Colocado em HOLD por", `${h.placedBy.name} · ${this.when(h.placedAt)}`], ["Status anterior (a restaurar)", h.previousStatusLabel]]
                .map(([l, v]) => `<label class="plh-field"><span>${this.esc(l)}</span><input class="plh-ctl" readonly value="${this.esc(v || "—")}"></label>`).join("")}
            </div>
            <div class="plh-note"><b>MOTIVO DO HOLD:</b> ${this.esc(h.reason)}${h.category ? ` · <b>Categoria:</b> ${this.esc(HOLD_CATEGORY_LABELS[h.category])}` : ""}</div>
            <div class="eng-hold-history">
              <b>HISTÓRICO</b>
              ${h.history.map(e => `<div class="eng-hold-entry"><span class="eng-muted">${this.esc(this.when(e.at))} · ${this.esc(e.by.name)}</span><strong>${this.esc(e.action)}</strong><span>${this.esc(e.detail)}</span></div>`).join("")}
            </div>
            ${canComment ? `
              <label class="plh-field"><span>COMENTÁRIO DA ENGENHARIA *</span><textarea class="plh-ctl" id="hold-comment" rows="4" placeholder="Orientação ao Inspetor sobre o HOLD.">${this.esc(m.comment)}</textarea></label>
              ${m.error ? `<div class="plh-errors">${this.esc(m.error)}</div>` : ""}
              <div class="plh-actions"><button class="plh-btn" id="btn-close-hold-2" ${m.busy ? "disabled" : ""}>Fechar</button><button class="plh-btn primary" id="btn-send-hold-comment" ${m.busy ? "disabled" : ""}>ENVIAR COMENTÁRIO</button></div>` : `<div class="plh-actions"><button class="plh-btn" id="btn-close-hold-2">Fechar</button></div>`}
          </div>
        </div>
      </div>`;
  }

  private async openHold(id: string): Promise<void> {
    try {
      const hold = await platform.documentHoldService.getHold(this.ctx(), id);
      this.holdModal = { open: !!hold, hold, comment: "", error: "", busy: false };
    } catch (e: any) {
      this.holdModal = { open: false, hold: null, comment: "", error: "", busy: false };
      this.error = e?.safeMessage || e?.message || "Erro ao abrir o HOLD.";
    }
    this.render();
  }

  private async sendHoldComment(): Promise<void> {
    const m = this.holdModal;
    if (!m.hold || m.busy) return;
    m.busy = true; m.error = ""; this.render();
    try {
      const updated = await platform.documentHoldService.comment(this.ctx(), m.hold.id, m.comment);
      this.holdModal = { open: true, hold: updated, comment: "", error: "", busy: false };
      await this.loadData();
      this.holdModal = { ...this.holdModal, open: true, hold: updated };
      this.render();
    } catch (e: any) {
      this.holdModal = { ...m, busy: false, error: e?.safeMessage || e?.message || "Erro ao comentar." };
      this.render();
    }
  }

  // --------------------------------------------------------------- HISTORY

  /**
   * FINALIZADOS — o que está tecnicamente fechado.
   *
   * Um documento aprovado pelo PLH mas ainda não assinado aparece com o
   * SUBSTATUS "FINALIZADO — AGUARDANDO ASSINATURA": ele fechou tecnicamente,
   * mas ainda falta um ato. Sem esse recorte, "aprovado" e "assinado"
   * pareciam a mesma coisa na lista, e ninguém via o que estava parado
   * esperando assinatura.
   */
  private renderFinalized(b: EngineeringBoard): string {
    const allRows = Array.isArray(b?.all) ? b.all : [];
    const finalized = allRows.filter(r =>
      r.workflowStatus === "APROVADO_PELO_PLH" || r.workflowStatus === "ASSINADO" || r.workflowStatus === "FINALIZADO");
    const awaitingSignature = finalized.filter(r => r.workflowStatus === "APROVADO_PELO_PLH");
    const closed = finalized.filter(r => r.workflowStatus !== "APROVADO_PELO_PLH");
    const sorted = (rows: EngineeringQueueRow[]) => platform.engineeringDashboardService.sort(Array.isArray(rows) ? rows : [], "NEWEST");

    return `
      <section class="eng-section">
        <div class="eng-section-head">
          <h2>FINALIZADO — AGUARDANDO ASSINATURA</h2>
          <small>Aprovados pelo PLH; o documento fechou tecnicamente e ainda falta a assinatura.</small>
        </div>
        ${this.renderQueueTable(sorted(awaitingSignature), "Substatus de FINALIZADOS — não é uma etapa nova do fluxo.")}
      </section>
      <section class="eng-section">
        <div class="eng-section-head">
          <h2>FINALIZADOS</h2>
          <small>Assinados e finalizados.</small>
        </div>
        ${this.renderQueueTable(sorted(closed), "Documentos fechados e assinados.")}
      </section>`;
  }

  private renderHistory(b: EngineeringBoard): string {
    const allRows = Array.isArray(b?.all) ? b.all : [];
    const approved = allRows.filter(r => r.workflowStatus === "APROVADO_PELO_PLH" || r.workflowStatus === "ASSINADO" || r.workflowStatus === "FINALIZADO");
    const clientName = (id: string) => { const c = this.clients.find((x: any) => x.id === id || x.code === id); return c ? (c.fantasyName || c.name || c.corporateName) : id; };
    return `
      <section class="eng-section">
        <div class="eng-section-head"><h2>HISTÓRICO</h2><small>Tudo o que já passou pela Engenharia: aprovados e assinados, HOLDs liberados e a visão completa dos Relatórios de Integridade.</small></div>
        ${this.renderQueueTable(this.filters.sort ? platform.engineeringDashboardService.sort(approved, "NEWEST") : approved, "Aprovados e assinados.")}
      </section>
      <section class="eng-section">
        <div class="eng-section-head"><h2>HOLDS LIBERADOS</h2></div>
        ${this.renderHoldTable(this.holdHistory, "")}
      </section>
      <section class="eng-section">
        <div class="eng-section-head"><h2>RELATÓRIOS DE INTEGRIDADE — TODOS OS STATUS</h2><small>Visão completa do RI no contexto (inclui rascunhos, fora da fila do PLH).</small></div>
        <div class="eng-table-wrap">
          <table class="eng-table">
            <thead><tr>${["Nº Relatório", "Cliente", "Status", "Criado em", "Ação"].map((h, i) => `<th ${i === 4 ? 'style="text-align:right;"' : ""}>${h.toUpperCase()}</th>`).join("")}</tr></thead>
            <tbody>
              ${this.riReports.length === 0 ? `<tr><td colspan="5" class="eng-empty-row">Nenhum Relatório de Integridade no contexto.</td></tr>` : [...this.riReports].sort((a: any, b2: any) => String(b2.createdAt || "").localeCompare(String(a.createdAt || ""))).map((r: any) => `
                <tr>
                  <td><strong>${this.esc(r.reportNumber || r.id)}</strong></td>
                  <td>${this.esc(clientName(r.clientId))}</td>
                  <!-- O mapa local de rótulos do RI vivia aqui, com um
                       fallback que imprimia o código cru em inglês (DRAFT,
                       WAITING_PLH) quando faltasse a entrada. Foi movido
                       para o catálogo central: um lugar só, e código
                       desconhecido aparece como está, nunca escondido.
                       (Sem crases neste comentário: ele está dentro de um
                       template literal, e uma crase aqui fecharia a string
                       e transformaria o texto seguinte em código.) -->
                  <td><span class="eng-status">${this.esc(translateStatus(r.status, INTEGRITY_REPORT_STATUS_LABELS))}</span></td>
                  <td class="nowrap">${this.esc(r.createdAt ? new Date(r.createdAt).toLocaleDateString("pt-BR") : "—")}</td>
                  <td style="text-align:right;"><button class="eng-btn btn-analyze" data-route="/engineering/workspace/${this.esc(r.id)}">Abrir Workspace</button></td>
                </tr>`).join("")}
            </tbody>
          </table>
        </div>
      </section>`;
  }

  // ------------------------------------------------------------------- AVR

  /**
   * AVR — seção própria: cards por etapa, contador "AVR — ANÁLISE DO PLH: N
   * PENDENTES" e a fila com ANALISAR. O PLH vê o que está atribuído a ele em
   * destaque; o resto do contexto do cliente fica visível para conferência.
   */
  private renderAvrSection(): string {
    if (!contextStore.hasPermission(PermissionCode.AvrRead)) return "";
    const counts = avrEngineeringCounts(this.avrs);
    const myProfessionalId = contextStore.get().professionalId || null;
    const list = this.avrs.filter(a => {
      switch (this.avrFilter) {
        case "QUEUE": return AVR_PLH_QUEUE_STATUSES.includes(a.status);
        case "ELABORACAO": return ["DRAFT", "CALCULATED", "OUTDATED", "VALIDATED"].includes(a.status);
        case "AGUARDANDO": return a.status === "AWAITING_PLH_REVIEW" || a.status === "PLH_REVIEWING";
        case "CORRECAO": return a.status === "CORRECTION_REQUESTED";
        case "APROVADA": return a.status === "PLH_APPROVED";
        default: return true;
      }
    });
    const clientName = (id: string) => { const c = this.clients.find((x: any) => x.id === id || x.code === id); return c ? (c.fantasyName || c.name || c.corporateName) : id; };
    // Mesma marcação dos cards da Visão Geral: um card só no sistema
    // inteiro. Manter o formato antigo aqui faria a aba AVR envelhecer
    // sozinha a cada ajuste do painel.
    const card = (title: string, count: number, key: typeof this.avrFilter, sub: string, icon: string, accent: string) => `
      <button class="eng-card avr-filter-card accent-${accent} ${this.avrFilter === key ? "active" : ""}" data-avr-filter="${key}" aria-pressed="${this.avrFilter === key}">
        <span class="eng-card-rail"></span>
        <span class="eng-card-ico">${this.icon(icon)}</span>
        <span class="eng-card-body">
          <span class="eng-card-value">${count}</span>
          <span class="eng-card-title">${this.esc(title)}</span>
          <span class="eng-card-sub">${this.esc(sub)}</span>
        </span>
        <span class="eng-card-go" aria-hidden="true">›</span>
      </button>`;
    return `
      <section class="eng-section">
        <div class="eng-section-head">
          <div><h2>AVR — AVALIAÇÃO DE VIDA REMANESCENTE</h2><small>Fila de análise do PLH e situação das AVRs do contexto.</small></div>
          <div id="avr-plh-counter" class="eng-counter">AVR — ANÁLISE DO PLH <span>${counts.pendentesPlh} PENDENTE${counts.pendentesPlh === 1 ? "" : "S"}</span></div>
        </div>
        <div class="eng-cards eng-cards-4">
          ${card("Em elaboração", counts.elaboracao, "ELABORACAO", "Ainda com o autor", "doc", "neutral")}
          ${card("Aguardando análise do PLH", counts.aguardandoPlh, "AGUARDANDO", "Na fila do responsável", "clock", "info")}
          ${card("Correção solicitada", counts.correcao, "CORRECAO", "Devolvida por item", "alert", "warn")}
          ${card("Análise aprovada", counts.aprovada, "APROVADA", "Parecer emitido", "check", "done")}
        </div>
        <div class="eng-table-wrap">
          <table id="avr-plh-queue" class="eng-table">
            <thead><tr>${["Nº AVR", "Cliente", "Projeto", "TAG / Equipamento", "Tipo de Equipamento", "Data de Envio", "Engenheiro Responsável", "PLH Responsável", "Status", "Ações"].map((h, i) => `<th ${i === 9 ? 'style="text-align:right;"' : ""}>${h.toUpperCase()}</th>`).join("")}</tr></thead>
            <tbody>
              ${list.length === 0 ? `<tr><td colspan="10" class="eng-empty-row">${this.avrFilter === "QUEUE" ? "Nenhuma AVR aguardando análise do PLH." : "Nenhuma AVR neste filtro."}</td></tr>` : list.map(a => {
                const mine = !!myProfessionalId && !!a.plhReview && String(a.plhReview.plhProfessionalId) === String(myProfessionalId);
                const inQueue = AVR_PLH_QUEUE_STATUSES.includes(a.status);
                return `
                  <tr class="${mine && inQueue ? "mine" : ""}">
                    <td><strong>${this.esc(a.avrNumber)}</strong><br><small class="eng-muted">Rev. ${this.esc(a.revision)}</small></td>
                    <td>${this.esc(a.clientDisplayName || clientName(a.clientId))}</td>
                    <td>${this.esc(a.projectContract || "—")}</td>
                    <td><strong>${this.esc(a.equipmentTag || "—")}</strong></td>
                    <td>${this.esc(a.equipmentTypeLabel || "—")}</td>
                    <td class="nowrap">${a.plhReview?.submittedAt ? this.esc(this.when(a.plhReview.submittedAt)) : "—"}</td>
                    <td>${this.esc(a.engineerResponsible?.name || a.createdBy || "—")}</td>
                    <td>${this.esc(a.plhReview?.plhName || "—")}${mine && inQueue ? ` <span class="eng-you">VOCÊ</span>` : ""}</td>
                    <td><span class="avr-status ${statusBadgeClass(a.status)}">${this.esc(AVR_STATUS_LABELS[a.status])}</span></td>
                    <td class="nowrap" style="text-align:right;">
                      <button class="eng-btn ${inQueue ? "primary" : ""} btn-avr-analyze" data-id="${this.esc(a.id)}">${inQueue ? "ANALISAR" : "Abrir análise"}</button>
                      <button class="eng-btn btn-avr-open" data-id="${this.esc(a.id)}">Elaboração</button>
                    </td>
                  </tr>`;
              }).join("")}
            </tbody>
          </table>
        </div>
      </section>`;
  }

  // ---------------------------------------------------------------- EVENTS

  private attachEvents(): void {
    this.querySelectorAll(".eng-tab").forEach(b => b.addEventListener("click", () => this.setTab((b as HTMLElement).dataset["tab"] as PanelTab)));
    this.querySelector("#btn-refresh")?.addEventListener("click", () => void this.loadData());
    this.querySelector("#btn-calcs")?.addEventListener("click", () => router.navigate("/engineering/calculations"));
    this.querySelectorAll(".eng-card[data-card]").forEach(c => c.addEventListener("click", () => {
      const key = (c as HTMLElement).dataset["card"] as EngineeringCardKey;
      this.filters.card = this.filters.card === key ? null : key;
      this.applyFilters();
    }));
    // Chips das Minhas Pendências, linhas do resumo de correções e atalhos
    // usam o MESMO contrato (`data-mine-tab` / `data-mine-card`): um
    // manipulador só, para os três não divergirem com o tempo.
    this.querySelectorAll("[data-mine-tab],[data-mine-card]").forEach(b => b.addEventListener("click", () => {
      const el = b as HTMLElement;
      const card = el.dataset["mineCard"] as EngineeringCardKey | "";
      const tab = el.dataset["mineTab"] as PanelTab | "";
      const route = el.dataset["route"];
      if (route) { router.navigate(route); return; }
      if (card) {
        this.filters.card = card;
        if (card !== "onHold") this.filters.plhProfessionalId = contextStore.get().professionalId || "";
      } else if (el.classList.contains("eng-chip")) {
        // O chip "Todas" limpa o recorte em vez de não fazer nada.
        this.filters.card = null;
        this.filters.plhProfessionalId = "";
      }
      if (tab) { this.setTab(tab); return; }
      this.applyFilters();
    }));
    this.querySelectorAll(".eng-quick-item[data-route]").forEach(b => b.addEventListener("click", () => {
      const route = (b as HTMLElement).dataset["route"];
      if (route) router.navigate(route);
    }));
    this.querySelector("#btn-clear-card")?.addEventListener("click", () => { this.filters.card = null; this.applyFilters(); });
    this.querySelector("#flt-period")?.addEventListener("change", e => {
      this.period = (e.target as HTMLSelectElement).value;
      const days = Number(this.period);
      if (!Number.isFinite(days) || days <= 0) {
        delete this.filters.from;
      } else {
        const since = new Date(Date.now() - days * 86_400_000);
        this.filters.from = localDayOf(since);
      }
      this.applyFilters();
    });
    // Uma linha da atividade leva ao documento; sem rota conhecida ela não
    // é clicável, em vez de ser um clique que não faz nada.
    this.querySelectorAll("[data-activity-route]").forEach(el => el.addEventListener("click", () => {
      const route = (el as HTMLElement).dataset["activityRoute"];
      if (route) router.navigate(route);
    }));
    this.querySelector("#btn-clear-filters")?.addEventListener("click", () => { this.filters = { documentType: "ALL", status: "ALL", priority: "ALL", sort: this.filters.sort || "PRIORITY", card: null }; this.applyFilters(); });
    const bind = (id: string, key: keyof EngineeringQueueFilters, event = "change") => this.querySelector(`#${id}`)?.addEventListener(event, e => { (this.filters as any)[key] = (e.target as HTMLInputElement | HTMLSelectElement).value; this.applyFilters(); });
    bind("flt-sort", "sort"); bind("flt-type", "documentType"); bind("flt-client", "client"); bind("flt-project", "project"); bind("flt-unit", "unitArea"); bind("flt-tag", "tag"); bind("flt-os", "osNumber"); bind("flt-subos", "subOsNumber"); bind("flt-inspector", "inspector"); bind("flt-plh", "plhProfessionalId"); bind("flt-status", "status"); bind("flt-from", "from"); bind("flt-to", "to"); bind("flt-priority", "priority"); bind("flt-search", "search");
    this.querySelectorAll(".btn-analyze").forEach(b => b.addEventListener("click", () => { const route = (b as HTMLElement).dataset["route"]; if (route) router.navigate(route); }));
    this.querySelectorAll(".btn-hold-analyze").forEach(b => b.addEventListener("click", () => { const id = (b as HTMLElement).dataset["hold"]; if (id) void this.openHold(id); }));
    const closeHold = () => { this.holdModal = { open: false, hold: null, comment: "", error: "", busy: false }; this.render(); };
    this.querySelector("#btn-close-hold")?.addEventListener("click", closeHold);
    this.querySelector("#btn-close-hold-2")?.addEventListener("click", closeHold);
    this.querySelector("#hold-comment")?.addEventListener("input", e => { this.holdModal.comment = (e.target as HTMLTextAreaElement).value; });
    this.querySelector("#btn-send-hold-comment")?.addEventListener("click", () => void this.sendHoldComment());
    this.querySelectorAll(".avr-filter-card").forEach(c => c.addEventListener("click", () => { const key = (c as HTMLElement).dataset["avrFilter"] as typeof this.avrFilter; this.avrFilter = this.avrFilter === key ? "QUEUE" : key; this.render(); }));
    this.querySelectorAll(".btn-avr-analyze").forEach(b => b.addEventListener("click", () => { const id = (b as HTMLElement).dataset["id"]; if (id) router.navigate(`/engineering/avr/${id}`); }));
    this.querySelectorAll(".btn-avr-open").forEach(b => b.addEventListener("click", () => { const id = (b as HTMLElement).dataset["id"]; if (id) router.navigate(`/integrity/avr/${id}`); }));
  }

  private styles(): string {
    return `
      ${PLH_REVIEW_UI_STYLES}
      ${HOLD_UI_STYLES}
      ${CORRECTION_UI_STYLES}
      /**
       * PALETA NEUTRA DO PAINEL (Correção 8).
       *
       * Os acentos saturados anteriores (#7c3aed roxo, #dc2626, #0ea5e9…)
       * davam a nove cards nove cores fortes: tudo gritava e nada se
       * destacava. Aqui a cor é sinalização discreta e escassa; a hierarquia
       * vem de tamanho, peso e espaço.
       */
      app-engineering-page {
        display:block;
        --eng-ink: var(--color-text-primary, #E7EAED);
        --eng-ink-soft: var(--color-text-secondary, #9BA4AE);
        --eng-ink-faint: var(--ia-fg-muted, #8A939D);
        --eng-line: var(--ia-line, rgba(255,255,255,0.08));
        --eng-line-soft: var(--ia-line, rgba(255,255,255,0.05));
        --eng-surface: var(--color-surface-primary, #12161A);
        --eng-surface-2: rgba(255,255,255,0.025);
        --eng-accent: var(--studio-eng-accent, var(--ia-eng-accent, #8C99A6));        /* cinza azulado — neutro padrão */
        --eng-accent-sand: var(--studio-eng-accent-sand, var(--ia-eng-accent-sand, #B29455));   /* areia/âmbar suave — atenção */
        --eng-accent-done: var(--studio-eng-accent-done, var(--ia-eng-accent-done, #6E9E7E));   /* verde suave — concluído */
        --eng-accent-warn: var(--studio-eng-accent-warn, var(--ia-eng-accent-warn, #B98A5E));   /* âmbar queimado — pendência */
        --eng-accent-info: var(--studio-eng-accent-info, var(--ia-eng-accent-info, #7E93A8));   /* azul neutro — em trânsito */
      }
      .eng-page { display:flex; flex-direction:column; gap:16px; padding:24px; background: var(--color-bg-primary); min-height:100%; }
      .eng-ico { width:16px; height:16px; flex:none; }
      .eng-header { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; flex-wrap:wrap; border-bottom:1px solid var(--eng-line); padding-bottom:16px; }
      .eng-header h1 { margin:0; font-size:1.55rem; font-weight:600; letter-spacing:-0.01em; color: var(--eng-ink); }
      .eng-header p { margin:5px 0 0 0; font-size:0.85rem; line-height:1.5; color: var(--eng-ink-soft); max-width:900px; }
      .eng-header-actions { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
      .eng-period { display:inline-flex; align-items:center; gap:7px; padding:0 10px; border:1px solid var(--eng-line); border-radius:8px; background: var(--eng-surface); color: var(--eng-ink-faint); }
      .eng-period select { background:transparent; border:none; color: var(--eng-ink); font-size:0.78rem; padding:9px 2px; cursor:pointer; outline:none; }
      .eng-tabs { display:flex; gap:2px; flex-wrap:wrap; border-bottom:1px solid var(--eng-line); }
      .eng-tab { display:inline-flex; align-items:center; gap:6px; background:transparent; border:none; border-bottom:2px solid transparent; padding:10px 14px; font-size:0.76rem; font-weight:600; letter-spacing:0.03em; color: var(--eng-ink-soft); cursor:pointer; transition: color .15s, background .15s; }
      .eng-tab:hover { color: var(--eng-ink); background: var(--eng-surface-2); }
      .eng-tab.active { color: var(--eng-ink); border-bottom-color: var(--eng-accent); background: rgba(255,255,255,0.03); }
      .eng-tab-badge { display:inline-block; min-width:18px; padding:1px 6px; border-radius:10px; background: rgba(255,255,255,0.10); color: var(--eng-ink); font-size:0.66rem; font-weight:700; text-align:center; }
      .eng-section { display:flex; flex-direction:column; gap:10px; }
      .eng-queue-status-note { font-size:0.72rem; letter-spacing:0.06em; color: var(--color-text-muted,#a1a1aa); padding:6px 10px; border-radius:6px; background: rgba(255,255,255,0.04); }
      .eng-section-head { display:flex; justify-content:space-between; align-items:flex-end; gap:12px; flex-wrap:wrap; }
      .eng-section-head h2 { margin:0; font-size:1rem; font-weight:700; color: var(--color-text-primary); letter-spacing:0.03em; }
      .eng-section-head small { color: var(--color-text-secondary); font-size:0.78rem; }
      .eng-inline { display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; }
      /* CARDS: número em destaque, título, subtítulo, ícone e seta. Sem
         gráfico interno e sem cor cheia — só um trilho de acento. */
      .eng-cards { display:grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap:12px; }
      .eng-cards-4 { grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); }
      .eng-card { position:relative; overflow:hidden; display:flex; align-items:center; gap:12px; text-align:left; background: var(--eng-surface); border:1px solid var(--eng-line); border-radius:10px; padding:14px 14px 14px 18px; cursor:pointer; color:inherit; font:inherit; transition: border-color .15s, background .15s; }
      .eng-card:hover { border-color: var(--ia-line, rgba(255,255,255,0.16)); background: rgba(255,255,255,0.035); }
      .eng-card.active { border-color: var(--eng-card-accent); background: rgba(255,255,255,0.05); }
      .eng-card-rail { position:absolute; top:0; left:0; width:3px; height:100%; background: var(--eng-card-accent); opacity:.85; }
      .eng-card.accent-neutral { --eng-card-accent: var(--eng-accent); }
      .eng-card.accent-info { --eng-card-accent: var(--eng-accent-info); }
      .eng-card.accent-warn { --eng-card-accent: var(--eng-accent-warn); }
      .eng-card.accent-sand { --eng-card-accent: var(--eng-accent-sand); }
      .eng-card.accent-done { --eng-card-accent: var(--eng-accent-done); }
      .eng-card-ico { display:flex; align-items:center; justify-content:center; width:38px; height:38px; flex:none; border-radius:9px; background: rgba(255,255,255,0.05); color: var(--eng-card-accent); }
      .eng-card-ico .eng-ico { width:19px; height:19px; }
      .eng-card-body { display:flex; flex-direction:column; min-width:0; flex:1; }
      .eng-card-value { font-size:1.65rem; font-weight:600; line-height:1.05; color: var(--eng-ink); font-variant-numeric: tabular-nums; }
      .eng-card-title { font-size:0.82rem; font-weight:600; color: var(--eng-ink); margin-top:2px; }
      .eng-card-sub { font-size:0.7rem; color: var(--eng-ink-faint); margin-top:1px; }
      .eng-card-go { color: var(--eng-ink-faint); font-size:1.1rem; flex:none; }

      /* BLOCOS do corpo: mesma moldura para pendências, correções,
         atividade e atalhos — o painel lê como um conjunto, não como
         quatro componentes diferentes. */
      .eng-grid-main { display:grid; grid-template-columns: minmax(0, 1.9fr) minmax(280px, 1fr); gap:14px; align-items:start; }
      .eng-block { display:flex; flex-direction:column; gap:12px; background: var(--eng-surface); border:1px solid var(--eng-line); border-radius:10px; padding:16px; min-width:0; }
      .eng-block-head { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; flex-wrap:wrap; }
      .eng-block-head h2 { display:flex; align-items:center; gap:8px; margin:0; font-size:0.95rem; font-weight:600; color: var(--eng-ink); letter-spacing:0; }
      .eng-block-head h2 .eng-ico { color: var(--eng-ink-faint); }
      .eng-block-head small { display:block; margin-top:3px; font-size:0.75rem; color: var(--eng-ink-faint); }
      .eng-link { background:none; border:none; color: var(--eng-ink-soft); font-size:0.73rem; font-weight:600; cursor:pointer; padding:2px 0; }
      .eng-link:hover { color: var(--eng-ink); }
      .eng-empty-block { margin:0; padding:14px; border:1px dashed var(--eng-line); border-radius:8px; font-size:0.78rem; line-height:1.5; color: var(--eng-ink-faint); }

      /* CHIPS de recorte rápido */
      .eng-chips { display:flex; gap:7px; flex-wrap:wrap; }
      .eng-chip { display:inline-flex; align-items:center; gap:7px; background: var(--eng-surface-2); border:1px solid var(--eng-line); border-radius:18px; padding:6px 12px; font-size:0.74rem; font-weight:500; color: var(--eng-ink-soft); cursor:pointer; transition: border-color .15s, color .15s; }
      .eng-chip:hover { border-color: var(--ia-line, rgba(255,255,255,0.18)); color: var(--eng-ink); }
      .eng-chip.active { background: rgba(255,255,255,0.07); border-color: var(--eng-accent); color: var(--eng-ink); }
      .eng-chip-n { min-width:18px; padding:0 6px; border-radius:9px; background: rgba(255,255,255,0.08); font-size:0.68rem; font-weight:700; text-align:center; font-variant-numeric: tabular-nums; }

      /* RESUMO lateral de correções */
      .eng-sum { display:flex; flex-direction:column; gap:6px; }
      .eng-sum-row { display:flex; align-items:center; gap:11px; width:100%; text-align:left; background:transparent; border:1px solid transparent; border-radius:8px; padding:9px 10px; cursor:pointer; color:inherit; font:inherit; transition: background .15s, border-color .15s; }
      .eng-sum-row:hover { background: var(--eng-surface-2); border-color: var(--eng-line); }
      .eng-sum-n { display:flex; align-items:center; justify-content:center; width:30px; height:30px; flex:none; border-radius:50%; border:1px solid currentColor; font-size:0.82rem; font-weight:700; font-variant-numeric: tabular-nums; }
      .eng-sum-n.accent-neutral { color: var(--eng-accent); }
      .eng-sum-n.accent-info { color: var(--eng-accent-info); }
      .eng-sum-n.accent-warn { color: var(--eng-accent-warn); }
      .eng-sum-n.accent-done { color: var(--eng-accent-done); }
      .eng-sum-ico { color: var(--eng-ink-faint); display:flex; }
      .eng-sum-text { display:flex; flex-direction:column; min-width:0; flex:1; }
      .eng-sum-text strong { font-size:0.8rem; font-weight:600; color: var(--eng-ink); }
      .eng-sum-text small { font-size:0.7rem; color: var(--eng-ink-faint); }
      .eng-sum-go { color: var(--eng-ink-faint); }

      /* ATIVIDADE RECENTE */
      .eng-feed { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; }
      .eng-feed li { display:flex; align-items:flex-start; gap:11px; padding:10px 6px; border-bottom:1px solid var(--eng-line-soft); }
      .eng-feed li:last-child { border-bottom:none; }
      .eng-feed li.is-clickable { cursor:pointer; border-radius:6px; }
      .eng-feed li.is-clickable:hover { background: var(--eng-surface-2); }
      .eng-feed-ico { color: var(--eng-ink-faint); display:flex; margin-top:1px; }
      .eng-feed-text { flex:1; min-width:0; font-size:0.79rem; line-height:1.45; color: var(--eng-ink-soft); }
      .eng-feed-text strong { color: var(--eng-ink); font-weight:600; }
      .eng-feed-text small { display:block; font-size:0.71rem; color: var(--eng-ink-faint); margin-top:2px; }
      .eng-feed-when { flex:none; font-size:0.71rem; color: var(--eng-ink-faint); white-space:nowrap; }

      /* AÇÕES RÁPIDAS */
      .eng-quick { display:flex; flex-direction:column; gap:6px; }
      .eng-quick-item { display:flex; align-items:center; gap:11px; width:100%; text-align:left; background: var(--eng-surface-2); border:1px solid var(--eng-line); border-radius:8px; padding:10px 12px; font-size:0.79rem; font-weight:500; color: var(--eng-ink-soft); cursor:pointer; transition: border-color .15s, color .15s; }
      .eng-quick-item:hover { border-color: var(--ia-line, rgba(255,255,255,0.18)); color: var(--eng-ink); }
      .eng-quick-item span:nth-child(2) { flex:1; min-width:0; }
      .eng-quick-ico { color: var(--eng-ink-faint); display:flex; }
      .eng-quick-go { color: var(--eng-ink-faint); }
      .eng-counter { display:flex; align-items:center; gap:10px; padding:8px 14px; border-radius:8px; background: var(--eng-surface-2); border:1px solid var(--eng-line); color: var(--eng-ink-soft); font-weight:600; font-size:0.78rem; }
      .eng-counter span { background: rgba(255,255,255,0.08); color: var(--eng-ink); border-radius:12px; padding:2px 10px; font-variant-numeric: tabular-nums; }
      .eng-filters { display:grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap:8px; align-items:end; background: var(--color-surface-primary); border:1px solid var(--color-graphite-200); border-radius:8px; padding:10px 12px; }
      .eng-filters.compact { grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); }
      .eng-field { display:flex; flex-direction:column; gap:3px; font-size:0.68rem; color: var(--color-text-secondary); min-width:0; }
      .eng-field input, .eng-field select { padding:7px 9px; border:1px solid var(--color-graphite-300); border-radius:6px; font-size:0.8rem; background: var(--color-surface-primary); color: var(--color-text-primary); width:100%; box-sizing:border-box; }
      .eng-field-wide { grid-column: span 2; }
      .eng-btn { display:inline-flex; align-items:center; gap:6px; background: rgba(255,255,255,0.04); border:1px solid var(--eng-line); border-radius:7px; padding:7px 12px; font-size:0.74rem; font-weight:600; cursor:pointer; color: var(--eng-ink-soft); white-space:nowrap; transition: border-color .15s, color .15s, background .15s; }
      .eng-btn:hover { border-color: var(--ia-line, rgba(255,255,255,0.18)); color: var(--eng-ink); }
      /* A ação principal se destaca por contraste de superfície, não por cor
         cheia: um botão roxo em cada linha da tabela competia com o dado. */
      .eng-btn.primary { background: var(--ia-bg-raised, #2B3A47); border-color: var(--ia-line, #3A4C5C); color: var(--eng-ink); }
      .eng-btn.primary:hover { background: var(--ia-bg-raised, #334554); }
      .eng-btn + .eng-btn { margin-left:6px; }
      .eng-chip { display:inline-flex; align-items:center; gap:6px; background:#ede9fe; color:#4c1d95; border-radius:14px; padding:4px 10px; font-size:0.74rem; font-weight:600; }
      .eng-chip-x { background:transparent; border:none; cursor:pointer; color:#4c1d95; font-weight:700; }
      .eng-caption { margin:0; font-size:0.76rem; color: var(--color-text-secondary); }
      .eng-table-wrap { background: var(--eng-surface); border-radius:9px; border:1px solid var(--eng-line); overflow-x:auto; }
      .eng-table { width:100%; border-collapse:collapse; text-align:left; font-size:0.8rem; }
      .eng-table th { padding:10px 12px; color: var(--eng-ink-faint); font-weight:600; white-space:nowrap; background: rgba(255,255,255,0.025); border-bottom:1px solid var(--eng-line); font-size:0.66rem; letter-spacing:0.05em; text-transform:uppercase; }
      .eng-table td { padding:11px 12px; border-bottom:1px solid var(--eng-line-soft); vertical-align:middle; color: var(--eng-ink); }
      .eng-table tr:last-child td { border-bottom:none; }
      .eng-table tr:hover td { background: rgba(255,255,255,0.025); }
      /* Sem faixa colorida por linha: a marca "VOCÊ" e o badge de HOLD já
         dizem o mesmo, e a faixa roxa/laranja poluía a leitura da tabela. */
      .eng-table tr.mine td { background: rgba(255,255,255,0.03); }
      .eng-table tr.onhold td { background: rgba(178,148,85,0.07); }
      .eng-table-compact td { padding:9px 12px; }
      .eng-table .nowrap { white-space:nowrap; }
      .eng-table .eng-wrap { max-width:360px; white-space:normal; }
      .eng-empty-row { padding:26px !important; text-align:center; color: var(--color-graphite-400); }
      .eng-empty { padding:40px; text-align:center; color: var(--color-text-secondary); }
      .eng-muted { color: var(--eng-ink-faint); font-size:0.72rem; }
      .eng-you { background: rgba(255,255,255,0.10); color: var(--eng-ink); border-radius:10px; padding:1px 7px; font-size:0.64rem; font-weight:700; letter-spacing:0.04em; }
      .eng-notice { padding:10px 14px; border-radius:6px; font-size:0.82rem; } .eng-notice-error { background: rgba(167,111,111,0.12); border:1px solid rgba(167,111,111,0.4); color: var(--ia-danger-fg, #d79e9e); }

      /**
       * BADGES — fundo tênue e texto suave sobre tema escuro. As cores
       * anteriores eram de tema claro (#ede9fe, #fee2e2, #d1fae5): sobre o
       * fundo grafite viravam manchas berrantes e a tabela ficava difícil
       * de varrer. Aqui cada estado tem tom próprio, mas nenhum grita.
       */
      .eng-prio { display:inline-block; padding:2px 8px; border-radius:10px; font-size:0.65rem; font-weight:700; letter-spacing:0.03em; border:1px solid transparent; }
      .prio-critica { background: rgba(167,111,111,0.16); color: var(--ia-danger-fg, #d79e9e); border-color: rgba(167,111,111,0.35); }
      .prio-alta { background: rgba(185,138,94,0.16); color: var(--ia-warning-fg, #d3a87c); border-color: rgba(185,138,94,0.32); }
      .prio-normal { background: rgba(255,255,255,0.06); color: var(--eng-ink-soft); border-color: var(--eng-line); }
      .eng-status, .avr-status { display:inline-block; padding:2px 9px; border-radius:11px; font-size:0.68rem; font-weight:600; letter-spacing:0.02em; background: rgba(255,255,255,0.06); color: var(--eng-ink-soft); border:1px solid var(--eng-line); white-space:nowrap; }
      /* aguardando = cinza azulado · reenviado = azul suave */
      .ws-aguardando_analise_plh { background: rgba(126,147,168,0.14); color: var(--ia-fg-soft, #9fb2c4); border-color: rgba(126,147,168,0.30); }
      .ws-reenviado_ao_plh { background: rgba(126,147,168,0.18); color: var(--ia-info-fg, #a8bdd1); border-color: rgba(126,147,168,0.38); }
      /* em análise = cinza neutro */
      .ws-em_analise_plh { background: rgba(255,255,255,0.07); color: var(--eng-ink); border-color: var(--ia-line, rgba(255,255,255,0.14)); }
      /* correções = âmbar suave */
      .ws-correcao_solicitada { background: rgba(185,138,94,0.16); color: var(--ia-warning-fg, #d3a87c); border-color: rgba(185,138,94,0.34); }
      .ws-correcao_em_andamento { background: rgba(178,148,85,0.14); color: var(--ia-warning-fg, #cbb182); border-color: rgba(178,148,85,0.30); }
      /* concluído = verde suave */
      .ws-aprovado_pelo_plh, .ws-minuta_validada { background: rgba(110,158,126,0.15); color: var(--ia-fg-soft, #8fbb9f); border-color: rgba(110,158,126,0.33); }
      /* HOLD = dourado discreto / areia */
      .ws-em_hold { background: rgba(178,148,85,0.20); color: var(--ia-warning-fg, #d9bd84); border-color: rgba(178,148,85,0.45); }
      .ws-assinado, .ws-finalizado, .ws-finalizado_aguardando_assinatura { background: rgba(255,255,255,0.05); color: var(--eng-ink-soft); border-color: var(--eng-line); }
      .ws-reprovado { background: rgba(167,111,111,0.16); color: var(--ia-danger-fg, #d79e9e); border-color: rgba(167,111,111,0.35); }
      .ws-minuta_pendente, .ws-aguardando_finalizacao { background: rgba(140,153,166,0.14); color: var(--ia-fg-soft, #a9b6c2); border-color: rgba(140,153,166,0.30); }
      .avr-status.status-outdated { background: rgba(167,111,111,0.16); color: var(--ia-danger-fg, #d79e9e); border-color: rgba(167,111,111,0.35); }
      .avr-status.status-review { background: rgba(126,147,168,0.14); color: var(--ia-fg-soft, #9fb2c4); border-color: rgba(126,147,168,0.30); }
      .avr-status.status-correction { background: rgba(185,138,94,0.16); color: var(--ia-warning-fg, #d3a87c); border-color: rgba(185,138,94,0.34); }
      .avr-status.status-approved { background: rgba(110,158,126,0.15); color: var(--ia-fg-soft, #8fbb9f); border-color: rgba(110,158,126,0.33); }
      .avr-status.status-signed { background: rgba(255,255,255,0.05); color: var(--eng-ink-soft); border-color: var(--eng-line); }
      .eng-hold-history { display:flex; flex-direction:column; gap:6px; font-size:0.78rem; }
      .eng-hold-entry { display:grid; grid-template-columns: 200px 170px 1fr; gap:8px; padding:6px 8px; border-left:2px solid #b45309; background: rgba(0,0,0,0.15); border-radius:4px; }
      /**
       * RESPONSIVIDADE. Em tela média o bloco lateral desce para baixo do
       * central em vez de espremer: "Minhas Pendências" com 7 colunas não
       * cabe ao lado de um card de 280px, e espremer produziria justamente
       * a quebra de leitura que o comando proíbe.
       */
      @media (max-width: 1280px) {
        .eng-grid-main { grid-template-columns: minmax(0, 1fr); }
        .eng-cards { grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); }
      }
      @media (max-width: 900px) {
        .eng-hold-entry { grid-template-columns: 1fr; }
        .eng-field-wide { grid-column: span 1; }
        .eng-page { padding:16px; }
        .eng-header-actions { width:100%; }
        .eng-period { flex:1; }
      }
      @media (max-width: 620px) {
        .eng-cards { grid-template-columns: minmax(0, 1fr); }
        .eng-feed li { flex-wrap:wrap; }
        .eng-feed-when { width:100%; padding-left:27px; }
      }
    `;
  }
}

if (!customElements.get("app-engineering-page")) {
  customElements.define("app-engineering-page", EngineeringPage);
}
